import { inject } from 'vue'
import { isString } from '../../utils'
import type { Option } from './types'

/**
 *  A helper function that determines if a value is an object and a label key present in that object.
 * @param {Object} value - The value to check.
 * @param {string} key - The key to check for in the object.
 * @returns {boolean}
 */
export const isObjectWithLabelKey = <Value, LabelKey extends string>(
  value: Value | Record<LabelKey, unknown>,
  key: LabelKey,
): value is Record<LabelKey, unknown> => {
  if (typeof value === 'object' && value !== null) {
    return key in value
  }

  return false
}

/**
 * Retrieves the display text for the selected value based on the source object.
 *
 * @param {Value} selectedValue - The value selected by the user.
 * @param {Object} source - The source object containing label and value keys and the options array.
 * @returns {string | undefined} - The label associated with the selected value, or undefined if no appropriate label is found.
 */
export const getSearchTextFromSource = <
  LabelKey extends string,
  ValueKey extends string,
  Value,
>(
  selectedValue: Value,
  source: {
    label: LabelKey
    value: ValueKey
    options: Record<LabelKey | ValueKey, unknown>[]
  },
): string | undefined => {
  if (typeof selectedValue === 'undefined') {
    return
  }

  const { label, value, options } = source

  const option = options.find((opt) => {
    if (isObjectWithLabelKey(selectedValue, value)) {
      return opt[value] === selectedValue?.[value]
    }

    return opt[value] === selectedValue
  })

  if (typeof option !== 'undefined' && isObjectWithLabelKey(option, label)) {
    return option?.[label]?.toString()
  }

  if (options.length && typeof option === 'undefined') {
    return
  }

  if (isObjectWithLabelKey(selectedValue, label)) {
    return selectedValue[label]?.toString()
  }

  return selectedValue?.toString()
}

/**
 * Determines whether the search text label is usable based on the provided
 * input and label availability."
 *
 * @param {string | undefined} searchInput - The input text for the search.
 * @param {Array<unknown> | string} [label] - The label or labels associated with the search input.
 * @returns {boolean} - returns true only when a search text is not available, and the label is present but not an array.
 */
export const hasValidSearchTextLabel = (
  searchInput: string | undefined,
  label?: unknown[] | string | number,
): boolean => !isString(searchInput) && Boolean(label) && !Array.isArray(label)

export type UseSelectContext = () => {
  selectedItemIndex: number
  selectedItem: Option | undefined
  activeIndex: number
  listItems: HTMLElement[]

  hideList: () => void
  showList: () => void
  focusOnSelectedItem: () => void
  focusAtIndex: (index: number) => void
  focusOnFirstItem: () => void
  focusOnLastItem: () => void
}

export const useSelectContext = (): UseSelectContext => {
  return inject('selectContext', () => ({
    selectedItemIndex: -1,
    selectedItem: undefined,
    activeIndex: -1,
    listItems: [],
    hideList: (): void => {},
    showList: (): void => {},
    focusOnSelectedItem: (): void => {},
    focusAtIndex: (): void => {},
    focusOnFirstItem: (): void => {},
    focusOnLastItem: (): void => {},
  }))
}
